/**
 * Input recording with the exact run-length semantics of the original replay recorder
 * `proc.write_key_press` (proc.as:3000-3035), producing typed `InputCommand`s instead of text.
 *
 * The game calls `record(tick, frame)` once per simulation tick with the current key state
 * (or `{ space: false, other: 'none' }` when the player has no control), and once more in the same tick
 * with `other: 'weapon'` / `'click'` when a weapon choice / click target is applied (proc.as:410-425).
 * `beginTurn()` corresponds to `ROUNDS_RECORD += "#"` (proc.as:980-986). As in the original, a key run that
 * is still held is *not* flushed at a turn boundary; it is flushed by the next differing tick.
 */
import { type InputCommand, type InputFrame, cloneFrame, frameToLegacyCode, legacyCodeToFrame } from './input';
import { encodeLegacyTurn } from './replay-codec';

export class KeyPressRecorder {
  /** Commands per recorded segment; segment 0 is the first turn. */
  readonly segments: InputCommand[][] = [[]];
  private keyLast = '';
  private keyDuration = 0;
  private keyIter = 0;

  /** proc.write_key_press. */
  record(tick: number, frame: { space: boolean; other: string; weapon?: number; range?: number; clickX?: number; clickY?: number }): void {
    const code = frameToLegacyCode(frame);
    if (frame.other === 'weapon' || frame.other === 'click') {
      const f = cloneFrame({ ...(frame as InputFrame), space: frame.space });
      this.append({ tick, frame: f, repeat: 0 });
      this.keyLast = '';
      return;
    }
    if (this.keyLast !== '') {
      if (this.keyLast === code) {
        this.keyDuration++;
      } else {
        this.append({ tick: this.keyIter, frame: legacyCodeToFrame(this.keyLast), repeat: this.keyDuration });
        this.keyLast = code;
        this.keyDuration = 0;
        this.keyIter = tick;
      }
    } else if (code !== '') {
      this.keyLast = code;
      this.keyDuration = 0;
      this.keyIter = tick;
    }
  }

  /** `ROUNDS_RECORD += "#"`: subsequent records go to a new segment. */
  beginTurn(): void {
    this.segments.push([]);
  }

  /**
   * Flushes a pending key run into the current segment. The original never does this explicitly (a run is
   * closed by the next differing tick); call it only when the recording ends.
   */
  flush(): void {
    if (this.keyLast !== '') {
      this.append({ tick: this.keyIter, frame: legacyCodeToFrame(this.keyLast), repeat: this.keyDuration });
      this.keyLast = '';
      this.keyDuration = 0;
    }
  }

  /** Current pending (not yet emitted) key run, if any. */
  pending(): InputCommand | null {
    if (this.keyLast === '') return null;
    return { tick: this.keyIter, frame: legacyCodeToFrame(this.keyLast), repeat: this.keyDuration };
  }

  /** The legacy ROUNDS record (`seg#seg#...`). */
  toLegacyRounds(): string {
    return this.segments.map((s) => encodeLegacyTurn(s)).join('#');
  }

  private append(cmd: InputCommand): void {
    this.segments[this.segments.length - 1].push(cmd);
  }
}
